import { useMemo, useState } from 'react';
import {
  Alert, Chip, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useOrganizacja } from '@/lib/useAnalytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * ADMINISTRACJA → ORGANIZACJA. Kto kogo obsługuje.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * TRZY EKRANY, TRZY RÓŻNE PYTANIA — i dlatego nie wolno ich zlewać:
 *
 *   SZKOŁY       ile sprzedano, komu, kiedy                → analityka
 *   GRAFIK       w jakie dni i do której godziny           → harmonogram
 *   ORGANIZACJA  która restauracja obsługuje którą szkołę  → relacja
 *
 * Zlanie ich dałoby jeden ekran, na którym nie widać ani liczb, ani harmonogramu, ani relacji.
 *
 * ┌─ CZEGO TEN EKRAN DZIŚ NIE ZROBI ────────────────────────────────────────────────────────────┐
 * │ PRZYPISANIA szkoły nie da się wykonać z konsoli administratora. API systemu głównego        │
 * │ wystawia tę operację wyłącznie roli dostawcy (`POST /api/Restaurant/current/school/{id}`)   │
 * │ i wyłącznie dla restauracji ZALOGOWANEGO — administrator nie ma odpowiednika.               │
 * │                                                                                             │
 * │ Nie obchodzimy tego własnym zapisem do bazy: powstałaby druga, niezgodna droga tworzenia   │
 * │ relacji, omijająca walidacje systemu głównego. Brak jest zgłoszony jako zależność, a ekran │
 * │ mówi o nim wprost zamiast pokazywać przycisk, który kończy się błędem.                      │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 */

const SKROT = ['pn', 'wt', 'śr', 'cz', 'pt', 'sb', 'nd'];

export function OrganizacjaPage() {
  const [restauracja, setRestauracja] = useState<number | ''>('');
  const { data, isLoading, error } = useOrganizacja(restauracja === '' ? undefined : restauracja);

  const przypisania = useMemo(() => data?.przypisania ?? [], [data]);

  /** Lista restauracji do filtra — z tego, co przyszło, bez osobnego zapytania. */
  const restauracje = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of przypisania) if (!m.has(p.restauracja_id)) m.set(p.restauracja_id, p.restauracja);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1], 'pl'));
  }, [przypisania]);

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Organizacja"
        opis="Która restauracja obsługuje którą szkołę. Harmonogram dni i godzin jest w Grafiku."
      />

      <Alert severity="info">
        <strong>Przypisania szkoły nie da się dziś wykonać z konsoli.</strong> System główny
        wystawia tę operację wyłącznie roli dostawcy i tylko dla restauracji zalogowanego —
        administrator nie ma odpowiednika. Odpięcie i zmiana dni działają.
      </Alert>

      {data && !data.zapis_podlaczony && (
        <Alert severity="warning">
          <strong>Działania operacyjne nie są w tym środowisku podłączone.</strong> Odczyt działa
          normalnie; każda próba zmiany odmówi jawnie, zamiast udawać, że się wykonała.
        </Alert>
      )}

      <Karta>
        <Stack direction="row" spacing={2} useFlexGap sx={{ p: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Restauracja"
            select
            size="small"
            sx={{ minWidth: 300 }}
            value={restauracja}
            onChange={(e) => setRestauracja(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <MenuItem value="">wszystkie</MenuItem>
            {restauracje.map(([id, nazwa]) => (
              <MenuItem key={id} value={id}>{nazwa}</MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {przypisania.length} {przypisania.length === 1 ? 'relacja' : 'relacji'}
          </Typography>
        </Stack>
      </Karta>

      {isLoading && <LoadingState />}
      {error ? <ErrorState error={error} context="Administracja → Organizacja." /> : null}

      {!isLoading && !error && przypisania.length === 0 && (
        <EmptyState
          title="Brak relacji szkoła–restauracja"
          description="Relacje pochodzą z repliki systemu głównego. Pusto znaczy albo, że żadna nie istnieje, albo że replikacja ich jeszcze nie przeniosła."
        />
      )}

      {przypisania.length > 0 && (
        <Karta>
          <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Relacje szkoła–restauracja">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Restauracja</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Szkoła</TableCell>
                  {SKROT.map((s, i) => (
                    <TableCell key={s} align="center" sx={{ fontWeight: 600 }}>
                      <Tooltip title={data?.dni_tygodnia[i]?.nazwa ?? ''}><span>{s}</span></Tooltip>
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Godzina zamknięcia</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {przypisania.map((p) => (
                  <TableRow key={`${p.restauracja_id}-${p.szkola_id}`} hover>
                    <TableCell>{p.restauracja}</TableCell>
                    <TableCell>{p.szkola}</TableCell>
                    {SKROT.map((_, i) => (
                      <TableCell key={i} align="center">
                        {p.dni_aktywne.includes(i)
                          ? <Chip size="small" label="✓" sx={{ minWidth: 32 }} />
                          : <Typography component="span" variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>}
                      </TableCell>
                    ))}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {/*
                        Godzina należy do Grafiku i tam się ją ustawia. Tutaj mówimy tylko,
                        czy jest jedna dla wszystkich dni — bo gdy nie jest, pokazanie
                        którejkolwiek udawałoby, że jest.
                      */}
                      {p.godzina_jednolita
                        ? <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                            jednolita — patrz Grafik
                          </Typography>
                        : <Chip size="small" color="warning" variant="outlined" label="różna w różne dni" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Karta>
      )}
    </Stack>
  );
}

export default OrganizacjaPage;
